import * as array from "./array";
import * as strSyntax from "./string-syntax";

export function isNull(str: string | null | undefined) {
    return str == null; // ^([a-zA-Z0-9]+\()
}

export function isNullOrEmpty(str: string | null | undefined) {
    return isNull(str) || str === "";
}

export function isNullOrWhitespace(str: null | undefined): false;
export function isNullOrWhitespace(str: string | any): boolean;
export function isNullOrWhitespace(str: string | null | undefined | any) {
    return isNull(str) || (typeof str === "string" && str.trim().length === 0);
}

// export function undefinedIfNullOrWhiteSpace(str: null | undefined): undefined;
// export function undefinedIfNullOrWhiteSpace(str: ""): undefined;
// export function undefinedIfNullOrWhiteSpace(str: string): string | undefined;
export function undefinedIfNullOrWhiteSpace(str: string | null | undefined) {
    if (isNullOrWhitespace(str)) return undefined;
    return str;
}

export function generateGuid() {
    const guid = ","
        .repeat(10)
        .split(",")
        .map(() =>
            (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1)
        )
        .join("");
    return guid.toLowerCase();
}

export function format(formatString: string, ...args: any[]) {
    const regex = new RegExp(/\{([0-9]+)\}/g);
    if (typeof formatString === "object" && formatString != null) {
        formatString = formatString + "";
    }
    if (formatString == null || formatString.matchAll == null) {
        const typeName = formatString === null ? "null" : typeof formatString;
        throw new Error("can't format " + typeName);
    }
    const iterator = formatString.matchAll(regex);

    let indexCorrection = 0;

    for (const value of iterator) {
        const placeholder = value[0];
        const index = parseInt(value[1]);

        if (index >= 0 && index < args.length) {
            let replacement = args[index];
            if (typeof replacement === "undefined" || replacement === null)
                replacement = "";
            if (
                typeof replacement !== "string" &&
                typeof replacement.toString === "function"
            )
                replacement = replacement.toString();
            if (value == null) throw new Error("value is missing");
            if (value.index == null) throw new Error("value is missing");
            const startIndex = indexCorrection + value.index;
            const endIndex = startIndex + placeholder.length;
            formatString =
                formatString.substring(0, startIndex) +
                replacement +
                formatString.substring(endIndex);
            indexCorrection =
                indexCorrection + (replacement.length - placeholder.length);
        }
    }

    return formatString;
}

export function toCharArray(str: string | null | undefined) {
    if (typeof str !== "string")
        throw new Error("Value provided is not a string");
    return str.split("").map((c: any) => c.charCodeAt(c));
}

export function fromCharArray(chars: number[]) {
    if (!Array.isArray(chars))
        throw new Error("Value provided is not an array");
    return String.fromCharCode.apply(null, chars);
}

export function compare(aValue: string, bValue: string) {
    const aChars = toCharArray(aValue);
    const bChars = toCharArray(bValue);

    const charDiff = array
        .init(Math.max(aChars.length, bChars.length), 0)
        .map((x, i) => {
            const aChar = aValue.charCodeAt(i);
            const bChar = bValue.charCodeAt(i);

            if (isNaN(aChar) && isNaN(bChar)) {
                return 0;
            }

            if ((isNaN(bChar) && !isNaN(aChar)) || aChar > bChar) {
                return 1;
            }
            if ((isNaN(aChar) && !isNaN(bChar)) || bChar > aChar) {
                return -1;
            }

            return 0;
        });
    const firstDiff = array.first(charDiff, (d: number) => d !== 0);
    if (typeof firstDiff === "number" && firstDiff !== 0)
        return ~~Math.min(Math.max(firstDiff, -1), 1);
    return 0;
}

export function split(str: string, pos: number, removeChar = true) {
    return [
        str.substring(0, pos),
        str.substring(pos + (removeChar === true ? 1 : 0)),
    ];
}

export function allIndexOf(str: string, searchString: string) {
    let offset = 0;
    let pos;
    const result = [];
    do {
        pos = str.indexOf(searchString, offset);
        if (pos === -1) break;
        result.push(pos);
        offset = pos + 1;
    } while (pos !== -1);
    return result;
}

export function getSyntaxLevels(str: string, open: string, close: string) {
    let level = 0;
    const levels = [];
    for (let i = 0; i < str.length; ++i) {
        const curCh = str[i];
        if (curCh === open) {
            levels.push(null);
            level++;
        } else if (curCh === close) {
            level--;
            levels.push(null);
        } else {
            levels.push(level);
        }
    }
    return levels;
}

export function syntaxSplit(str: string, open: string, close: string) {
    if (typeof str !== "string")
        throw new Error("Provided argument must be of type: string");
    if (str.length === 0) return [];

    const levels = getSyntaxLevels(str, open, close);
    const compressedLevels = array
        .compressValues(levels)
        .filter((l) => l.value !== null);

    if (compressedLevels.length === 1 && compressedLevels[0].value === 0) {
        return [str];
    }

    // maybe TODO fix if syntax does not end at same level

    let lastLevel = -1;

    const leveler = new strSyntax.SyntaxLeveler();

    for (let i = 0; i < compressedLevels.length; i++) {
        const currentLevel = compressedLevels[i];
        leveler.insertLevel(
            currentLevel.value - lastLevel,
            str.substring(currentLevel.start, currentLevel.end)
        );
        lastLevel = currentLevel.value;
    }

    return leveler.toArray();
}

export function flattenSyntaxSplit(
    data: any[],
    open = "",
    close = "",
    level = 0
) {
    if (typeof data === "string") return data;
    if (!Array.isArray(data)) throw new Error("Data is not string or array");

    let result = "";

    if (level !== 0) {
        result += open;
    }

    for (const item of data) {
        result += flattenSyntaxSplit(item, open, close, level + 1);
    }

    if (level !== 0) {
        result += close;
    }

    return result;
}

export function cssUrl(url: string, escape = true) {
    if (escape === true) url = escapeCssUrl(url);
    return `url("${url}")`;
}

export function escapeCssUrl(url: string) {
    return encodeURI(url);
}

export function normalize(str: string) {
    if (typeof str !== "string") return str;
    return str.replace(/\r/g, "");
}

export function splitLines(str: string) {
    const items = normalize(str)
        .split("\n")
        .map((item, index, array) => ({
            first: index === 0,
            index,
            last: index === array.length - 1,
            value: item,
        }));
    return items;
}
